import { useState } from 'react'

import { ДИНАМОМЕТРЫ, G, dynamometerReading, ru, scaleDivision } from '../../lib/physics'
import { Choice, зигзаг } from './common'

/**
 * Динамометр: предел измерения и цена деления (урок 35). Ученик подвешивает грузы,
 * сам считает цену деления и снимает показание; перегрузка — отдельный урок.
 */

const ГРУЗЫ_Г = [20, 50, 100, 200, 500, 1000] as const
const ШКАЛА_ВЕРХ = 50
const ШКАЛА_ДЛИНА = 200

/** Число из поля ввода: запятая или точка; пусто или мусор — null. */
function число(s: string): number | null {
  const x = Number(s.trim().replace(',', '.'))
  return s.trim() === '' || !Number.isFinite(x) ? null : x
}

export default function DynamometerLab() {
  const [номер, setНомер] = useState(0)
  const [грузы, setГрузы] = useState<number[]>([])
  const [цена, setЦена] = useState('')
  const [показ, setПоказ] = useState('')
  const [проверено, setПроверено] = useState(false)

  const прибор = ДИНАМОМЕТРЫ[номер]
  const деление = прибор.подписи / прибор.делений
  const всегоДелений = Math.round(прибор.предел / деление)
  const массаГ = грузы.reduce((a, b) => a + b, 0)
  const сила = (массаГ / 1000) * G
  const { reading, overload } = dynamometerReading(сила, прибор.предел, деление)

  const yЗнач = (f: number) => ШКАЛА_ВЕРХ + (f / прибор.предел) * ШКАЛА_ДЛИНА
  const указатель = yЗнач(reading)
  const делёж: number[] = []
  for (let i = 0; i <= всегоДелений; i++) делёж.push(i)

  const сменитьПрибор = (n: number) => {
    setНомер(n)
    setГрузы([])
    setЦена('')
    setПоказ('')
    setПроверено(false)
  }

  const верноЦена = (() => {
    const x = число(цена)
    return x !== null && Math.abs(x - scaleDivision(2 * прибор.подписи, прибор.подписи, прибор.делений)) < 1e-9
  })()
  const верноПоказ = (() => {
    const x = число(показ)
    return x !== null && Math.abs(x - reading) <= деление / 2 + 1e-9
  })()

  return (
    <div className="card">
      <div className="lab-two">
        <svg className="lab-svg" viewBox="0 0 220 330" role="img" aria-label="Динамометр с грузами">
          <rect x={60} y={20} width={100} height={ШКАЛА_ДЛИНА + 50} rx={8} className="fill-cabin ink" strokeWidth={2} />
          <line x1={110} y1={8} x2={110} y2={20} className="ink" strokeWidth={3} />
          <circle cx={110} cy={6} r={5} className="ink" fill="none" strokeWidth={2} />
          <polyline points={зигзаг(88, 30, указатель - 30, 10, 6)} className="ink-band" strokeWidth={1.6} fill="none" />
          {/* шкала */}
          {делёж.map((i) => {
            const подпись = Math.abs((i * деление) / прибор.подписи - Math.round((i * деление) / прибор.подписи)) < 1e-9
            const y = yЗнач(i * деление)
            return (
              <g key={i}>
                <line x1={118} y1={y} x2={подпись ? 134 : 126} y2={y} className="ink" strokeWidth={подпись ? 1.4 : 0.7} />
                {подпись && <text x={138} y={y + 3.5} className="lab-tick">{ru(i * деление, прибор.подписи < 1 ? 1 : 0)}</text>}
              </g>
            )
          })}
          <text x={138} y={ШКАЛА_ВЕРХ - 10} className="lab-tick">Н</text>
          {/* указатель и крюк */}
          <line x1={80} y1={указатель} x2={124} y2={указатель} className={overload ? 'ink-heavy' : 'ink-el'} strokeWidth={3} />
          <line x1={88} y1={указатель} x2={88} y2={ШКАЛА_ДЛИНА + 80} className="ink" strokeWidth={2} />
          <path d={`M88 ${ШКАЛА_ДЛИНА + 80} q0 10 -8 10`} className="ink" fill="none" strokeWidth={2} />
          {грузы.length > 0 && (
            <>
              <rect x={66} y={ШКАЛА_ДЛИНА + 92} width={44} height={22} rx={3} className="fill-body ink" strokeWidth={1.5} />
              <text x={88} y={ШКАЛА_ДЛИНА + 107} textAnchor="middle" className="lab-label">
                {массаГ >= 1000 ? `${ru(массаГ / 1000, 2)} кг` : `${массаГ} г`}
              </text>
            </>
          )}
        </svg>

        <div>
          <div className="lab-sub">Подвесь грузы</div>
          <div className="row-wrap">
            {ГРУЗЫ_Г.map((г) => (
              <button key={г} className="btn" onClick={() => { setГрузы((x) => [...x, г]); setПроверено(false) }}>
                + {г >= 1000 ? '1 кг' : `${г} г`}
              </button>
            ))}
            <button className="btn" onClick={() => { setГрузы([]); setПроверено(false) }}>снять все</button>
          </div>
          {overload && (
            <div className="lab-verdict">
              ⚠️ Перегрузка: груз тянет на {ru(сила, 1)} Н, а предел прибора — {прибор.предел} Н.
              Пружину так можно испортить — возьми прибор с большим пределом.
            </div>
          )}

          <label className="lab-slider">
            <span>Цена деления этого прибора, Н</span>
            <input inputMode="decimal" value={цена} placeholder="например, 0,5" onChange={(e) => { setЦена(e.target.value); setПроверено(false) }} />
          </label>
          <label className="lab-slider">
            <span>Показание динамометра, Н</span>
            <input inputMode="decimal" value={показ} placeholder="сколько показывает указатель" onChange={(e) => { setПоказ(e.target.value); setПроверено(false) }} />
          </label>
          <div className="row-wrap" style={{ marginTop: 10 }}>
            <button className="btn btn-primary" onClick={() => setПроверено(true)}>Проверить</button>
          </div>
          {проверено && (
            <div className="lab-verdict-list">
              <div className={верноЦена ? 'lab-verdict ok' : 'lab-verdict'}>
                {верноЦена
                  ? `✅ Цена деления ${ru(деление, 2)} Н.`
                  : `Цена деления: возьми два соседних подписанных значения (${ru(прибор.подписи, 1)} и ${ru(2 * прибор.подписи, 1)} Н), раздели разность на число делений между ними (${прибор.делений}).`}
              </div>
              <div className={верноПоказ ? 'lab-verdict ok' : 'lab-verdict'}>
                {верноПоказ
                  ? `✅ Показание ${ru(reading, 2)} Н.`
                  : `Показание: сосчитай деления от ближайшей подписи до указателя и умножь на цену деления.`}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="lab-sub">Прибор</div>
      <Choice
        options={ДИНАМОМЕТРЫ.map((d, i) => ({ value: i, label: `на ${d.предел} Н` }))}
        value={номер}
        onChange={сменитьПрибор}
      />
      <p className="muted small">
        Предел измерения — наибольшая сила, которую прибор может показать. Цена деления —
        сколько ньютонов приходится на одно деление. Гиря 1 кг тянет с силой около 10 Н.
      </p>
    </div>
  )
}
